/** NikahElegant — RSVP. CTA with optional external link + inline acceptance form. */

import React from 'react';
import { motion } from 'framer-motion';
import { Mail, CalendarClock } from 'lucide-react';
import { NIKAN_EASE } from '../theme';
import type { NikahContent } from '../content';
import { useRsvp } from '@/src/modules/rsvp';

export function RsvpSection({
  content,
  invitationId,
  isLive = false,
}: {
  content: NikahContent['rsvp'];
  invitationId: string | null;
  isLive?: boolean;
}) {
  const [name, setName] = React.useState('');
  const [attending, setAttending] = React.useState<'yes' | 'no' | ''>('');

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const sent = state === 'success';
  const sending = state === 'loading';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !attending || sending) return;
    await submit({
      guestName: name.trim(),
      status: attending === 'yes' ? 'ACCEPTED' : 'DECLINED',
      headcount: 1,
    });
  };

  return (
    <section
      id="rsvp"
      className="px-6 py-20 text-center"
      style={{ background: 'var(--nikah-ivory-dark)', color: 'var(--nikah-ink)' }}
    >
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: NIKAN_EASE }}
        className="text-sm uppercase tracking-[0.3em] text-[var(--nikah-emerald)]"
      >
        {content.label}
      </motion.p>
      <h2 className="mt-2 font-serif text-4xl font-semibold text-[var(--nikah-emerald-dark)]">
        {content.title}
      </h2>
      <p className="mx-auto mt-4 max-w-xl text-[var(--nikah-muted)]">{content.description}</p>

      <div className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--nikah-emerald)]">
        <CalendarClock className="h-4 w-4" />
        Kindly respond by {content.deadline}
      </div>

      {content.externalLink ? (
        <div className="mt-8">
          <a
            href={content.externalLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--nikah-emerald)] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--nikah-ivory)] shadow-lg transition-transform hover:scale-105"
          >
            <Mail className="h-4 w-4" /> RSVP Here
          </a>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mx-auto mt-8 max-w-md space-y-4">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full rounded-lg border border-[var(--nikah-border)] bg-white px-4 py-3 text-center outline-none focus:border-[var(--nikah-emerald)]"
          />
          <div className="flex justify-center gap-3">
            {(['yes', 'no'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setAttending(opt)}
                className={`rounded-full px-6 py-2 text-sm font-semibold capitalize transition ${
                  attending === opt
                    ? 'bg-[var(--nikah-emerald)] text-[var(--nikah-ivory)]'
                    : 'border border-[var(--nikah-border)] text-[var(--nikah-muted)]'
                }`}
              >
                {opt === 'yes' ? 'Joyfully accepts' : 'Regretfully declines'}
              </button>
            ))}
          </div>
          {state === 'error' && errorMessage && (
            <p className="text-sm font-medium text-red-600">{errorMessage}</p>
          )}
          <button
            type="submit"
            disabled={!name || !attending || sending || sent}
            className="w-full rounded-full bg-[var(--nikah-gold)] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--nikah-emerald-dark)] shadow-lg transition-transform hover:scale-[1.02] disabled:opacity-40"
          >
            {sent ? 'Thank you!' : sending ? 'Sending…' : 'Send RSVP'}
          </button>
        </form>
      )}
    </section>
  );
}
